/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { BaseComponent } from '../../../base.component';
import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { UtilsService } from '../../../../services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-rbac-endpoints',
    templateUrl: './rbac-endpoints.component.html',
    styleUrls: ['./rbac-endpoints.component.scss'],
})
export class RbacEndpointsComponent extends BaseComponent implements OnInit {
    /**
     * Collect all the select all checkboxes.
     */
    @ViewChildren('selectAlls')
    public selectAlls: QueryList<any> = new QueryList<any>();

    /**
     * Incoming list of endpoints.
     */
    @Input() set endpoints(endpoints: any[]) {
        this._endpoints = endpoints;
    }

    /**
     * Incoming list of groups for the endpoints (optional).
     * e.g. [{ path: '/api/v1/:namespace/adcs/', friendlyName: 'ADCs' }]
     * The above example will group all endpoints whose path starts with '/api/v1/:namespace/adcs/'
     * and will add the friendly as an accordion title.
     */
    @Input() public grouping: any[] = [];

    /**
     * Give parent component the ability to collapse the accordions.
     * @param state
     */
    @Input() set forceCollapse(state: boolean) {
        if (state) {
            this.forceCollapseAll = true;
        }
    }

    /**
     * Give parent component the ability to disable this component.
     */
    @Input() set isDisabled(state: boolean) {
        this.forceCollapseAll = state;
        this.enableToggleOfAccordions = !state;
        if (state) {
            this.formGroup.disable();
            this.resetToggleState();
        } else {
            this.formGroup.enable();
        }
    }

    /**
     * Send endpoints object to parent form.
     */
    @Output() public endpointsEventEmitter: EventEmitter<any> = new EventEmitter();

    /**
     * Accordion title height, used on tab title and counter.
     */
    public accordionTitleHeight: number = 55;

    /**
     * The main formGroup variable
     */
    public checkboxFormGroup: FormGroup;

    /**
     * The main formGroup variable
     */
    public formGroup: FormGroup;

    /**
     * Give user option to expand all accordions.
     */
    public expandAll: boolean = false;

    /**
     * Force close the accordions.
     */
    public forceCollapseAll: boolean = false;

    /**
     * Enable/Disable the toggling of the accordions.
     */
    public enableToggleOfAccordions: boolean = true;

    /**
     * Store the toggle state of each accordion
     */
    public toggleState: object = {};

    /**
     * Counter to debug endpoints (dev only).
     */
    public pathCounter = {
        displayed: 0,
        total: 0
    };

    public formattedGroups = [];

    /**
     * Filtered form groups.
     */
    public filteredFormGroups: object = {};

    /**
     * Store changes in the endpoints for row counter.
     * @private
     */
    private storedChanges: any;

    /**
     * Store the initial form data to check for changes.
     * @private
     */
    private initialFormData: object = {};

    /**
     * Endpoints getter and setter variable.
     * @private
     */
    private _endpoints: any[] = [];

    constructor(
        private formBuilder: FormBuilder,
        private utilsService: UtilsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.setupFormGroup();
        this.setUpFormSubscribers();
        this.setUpPageData(this.endpoints);
    }

    get endpoints(): any[] {
        return this._endpoints;
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get formEndpoints(): FormArray {
        return this.formGroup.get('endpoints') as FormArray;
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get formCheckBoxes(): FormArray {
        return this.checkboxFormGroup.get('checkboxes') as FormArray;
    }

    /**
     * Toggle accordions.
     */
    public toggleAccordions(): void {
        this.expandAll = !this.expandAll;
    }

    /**
     * Store the toggle state of accordion.
     * @param state
     * @param index
     */
    public storeToggledAccordions(state: any, index: number): void {
        this.toggleState[index] = state;
    }

    /**
     * Helper to determine if the accordion counter should show
     * @param group
     * @param index
     */
    public showAccordionCounter(group: { service: string; }, index: number): boolean {
        return !this.toggleState[index] && !!this.getCounterByGroup(group.service);
    }

    /**
     * Get selected group counter.
     * @param service
     */
    public getCounterByGroup(service: string): number {
        return this.storedChanges.filter((item: any): boolean => item.service === service)?.length;
    }

    /**
     * Get form control for checkboxes.
     * @param endpoint
     * @param method
     */
    public getFormControlForCheckboxes(endpoint: FormGroup, method: string | number): FormControl {
        return endpoint['controls']['methods']['controls'][method];
    }

    /**
     * Toggle column.
     * @param method
     * @param group
     * @param index
     */
    public toggleColumn(method: string, group: any, index: number): void {
        const row: any = this.checkboxFormGroup.get('checkboxes')['controls'][index];
        const titleFormGroup: AbstractControl = row.get(method);
        titleFormGroup.setValue(!titleFormGroup.value);
        if (method === 'ALL') {
            this.toggleOtherCheckboxes(row, titleFormGroup);
        }

        const serviceFormGroups: any = this.filteredFormGroups[group.service];
        for (const formGroup of serviceFormGroups) {
            if (method === 'ALL') {
                this.setSelectAll(titleFormGroup, formGroup, group.service);
            } else {
                const methodFormGroup: any = formGroup.get(`methods.${method}`);
                this.setMethodValue(methodFormGroup, titleFormGroup);

                if (method === 'PUT') {
                    const patch: any = formGroup.get('methods.PATCH');
                    this.setMethodValue(patch, titleFormGroup);
                }
            }

            // Check each row of checkboxes to see if they have all been checked and if so check the select all checkbox.
            this.checkboxHandler(formGroup.get('index').value, '', row);
        }

        // Toggle GET with others methods as there is no point being able to edit if you can't see it.
        const getNotSet: boolean = serviceFormGroups.some((item: any): any => item.value.methods['GET'] === false);
        if (method !== 'GET' && method !== 'ALL' && getNotSet) {
            this.toggleColumn('GET', group, index);
        }
    }

    /**
     * Toggle other checkboxes so when they are next clicked they are in the correct state.
     * @param row
     * @param titleFormGroup
     * @private
     */
    private toggleOtherCheckboxes(row: any, titleFormGroup: AbstractControl<any, any>): void {
        const controls: any = row.controls;
        for (const control in controls) {
            if (controls.hasOwnProperty(control)) {
                if (control !== 'ALL') {
                    controls[control].setValue(titleFormGroup.value);
                }
            }
        }
    }

    /**
     * Set all checkboxes across a group.
     * @private
     */
    private setSelectAll(titleFormGroup: AbstractControl<any, any>, formGroup: any, service: any): void {
        const methods = formGroup.get('methods').controls;
        for (const method in methods) {
            if (methods.hasOwnProperty(method)) {
                this.setMethodValue(methods[method], titleFormGroup);
            }
        }

        // Check selectAll checkboxes
        const elements: any[] = this.selectAlls.toArray();
        for (const element of elements) {
            if (element.nativeElement.groupName === service) {
                element.nativeElement.checked = titleFormGroup.value;
            }
        }
    }

    /**
     * Set method value
     * @param method
     * @param titleFormGroup
     * @private
     */
    private setMethodValue(method: any, titleFormGroup: AbstractControl<any, any>): void {
        if (method?.enabled) {
            method.setValue(titleFormGroup.value);
        }
    }

    /**
     * Select all toggle.
     * @param index
     * @param state
     */
    public selectAll(index: number, state: any): void {
        const row: AbstractControl = this.formEndpoints.controls[index];
        if (row) {
            const methods: string[] = ['POST', 'GET', 'PUT', 'PATCH', 'DELETE'];
            methods.forEach(method => {
                const field: AbstractControl = row.get(`methods.${method}`);
                if (field && !field.disabled) {
                    field.setValue(state.checked);
                    field.markAsDirty();
                }
            });
        }
    }

    /**
     * Show hide PUT methods
     * @param endpoint
     */
    public showHidePut(endpoint: any): boolean {
        return endpoint.get('methods.PUT').enabled;
    }

    /**
     * Update hidden method - the form will only ever show PUT or PATCH but not both. To keep the form valid the hidden
     * form field needs to be updated to the same value as the visible form field.
     * @param index
     * @param visibleMethod
     * @param hiddenMethod
     */
    public updateHiddenMethod(index: number, visibleMethod: 'PUT' | 'PATCH', hiddenMethod: 'PUT' | 'PATCH'): void {
        const rowControls = this.formEndpoints.controls[index]['controls'].methods.controls;
        const hiddenControl = rowControls[hiddenMethod];
        hiddenControl.setValue(rowControls[visibleMethod].value);
    }

    /**
     * Check and uncheck select all based on row values.
     * @param index
     * @param method
     * @param selectAllRow
     */
    public checkboxHandler(index: number, method: string, selectAllRow?: any): void {
        const row: AbstractControl = this.formEndpoints.controls[index];
        if (row) {
            const timerId: any = setTimeout((): void => {
                if (!selectAllRow && method !== 'GET') {
                    // TODO created a ticket to finish this
                    // https://linear.app/loadbalancer/issue/END-169/rbac-set-get-to-true-if-another-state-is-checked
                    // this.setGetState(row, method);
                }

                const rowState: boolean = this.getRowState(row);
                const elements: any[] = this.selectAlls.toArray();
                const selectAllIndex: number = this.findSelectAllCheckbox(elements, index);
                if (selectAllIndex !== -1) {
                    elements[selectAllIndex].nativeElement.checked = rowState;
                    if (selectAllRow) {
                        selectAllRow.get('ALL').setValue(rowState);
                    }
                }

                clearTimeout(timerId);
            }, 0); // required as there is a race condition with form.
        }
    }

    /**
     * Set the GET methods to true if one of the other methods is set, no point not being able to read when you can edit.
     * @param row
     * @param method
     * @private
     */
    private setGetState(row: AbstractControl, method: string): void {
        const rowsMethodStates = row.get('methods').value;
        if (!rowsMethodStates.GET && row.get('methods.GET').enabled &&
            (rowsMethodStates.POST || rowsMethodStates.PUT || rowsMethodStates.PATCH || rowsMethodStates.DELETE)
        ) {
            row.get('methods.GET').setValue(true);
        } else if (row.get('methods.GET').enabled) {
            const value = method === 'PATCH' ? rowsMethodStates.PUT : rowsMethodStates.toggle;
            row.get('methods.GET').setValue(value);
        }
    }

    /**
     * Get row state.
     * @param row
     * @private
     */
    private getRowState(row: any): boolean {
        const activeControls: any = [];
        const controls = row.controls.methods.controls;
        for (const method in controls) {
            if (controls.hasOwnProperty(method) && method !== 'path') {
                if (!controls[method].disabled) {
                    activeControls.push({ name: method, control: controls[method] });
                }
            }
        }

        return activeControls.every((item: any) => item?.control?.value);
    }

    /**
     * Find select all checkbox for row.
     * @param elements
     * @param index
     * @private
     */
    private findSelectAllCheckbox(elements: any, index: number): number {
        return elements.findIndex(
            (item: { nativeElement: { id: string; }; }): boolean => item.nativeElement.id === 'select' + index
        );
    }

    /**
     * Set up the form builder.
     * @private
     */
    private setupFormGroup(): void {
        this.checkboxFormGroup = this.formBuilder.group({
            checkboxes: this.formBuilder.array([]),
        });

        this.formGroup = this.formBuilder.group({
            endpoints: this.formBuilder.array([]),
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((values): void => {
                if (this.initialFormData && this.initialFormData['endpoints']) {
                    this.storedChanges = this.findCheckedEndpoints(values.endpoints);
                    let clean: boolean;
                    const formattedEndpoints = this.formatEndpoints(values['endpoints']);
                    if (JSON.stringify(this.initialFormData['endpoints']) === JSON.stringify(formattedEndpoints)) {
                        this.formGroup.markAsPristine();
                        clean = true;
                    } else {
                        this.formGroup.markAsDirty();
                        clean = false;
                    }

                    const cleanedValues = this.formGroup.value.endpoints.map(({ path, methods, service }) => ({
                        path,
                        methods,
                        service
                    }));

                    this.endpointsEventEmitter.next({ values: cleanedValues, clean: clean });
                }
            });
    }

    /**
     * Format endpoints.
     * @param endpoints
     * @private
     */
    private formatEndpoints(endpoints: any[]): any[] {
        for (const endpoint of endpoints) {
            endpoint.methods = this.utilsService.cleanObject(endpoint.methods);
        }

        return endpoints;
    }

    /**
     * Find checked endpoint methods.
     * @param values
     * @private
     */
    private findCheckedEndpoints(values: any[]): any[] {
        return values.filter(item => Object.values(item?.methods)?.includes(true));
    }

    /**
     * Set up page data.
     * @param endpoints
     * @private
     */
    private setUpPageData(endpoints: any): void {
        if (endpoints?.length) {
            let index: number = 0;
            for (const endpoint of endpoints) {
                this.addEndpoint(endpoint, index);
                this.checkboxHandler(index, '');
                index++;
            }
        }

        this.setUpGrouping();
        this.initialFormData = this.formGroup.value;
        this.formGroup.updateValueAndValidity();
    }

    /**
     * Set up grouping in the view.
     * @private
     */
    private setUpGrouping(): void {
        let index: number = 0;

        for (const group of this.grouping) {
            this.toggleState[index] = false;
            const filteredEndpoints: any[] = this.filterEndpointsByGroup(group.service);
            this.filteredFormGroups[group.service] = filteredEndpoints;

            const foundExistingService = this.formattedGroups.findIndex(item => item.service === group.service);
            if (foundExistingService === -1) {
                const allSelected: boolean = this.allValuesAreTrue(this.filteredFormGroups[group.service]);
                this.createCheckboxFormGroupEntry(allSelected);
                this.formattedGroups.push(group);
                this.pathCounter.displayed = this.pathCounter.displayed + filteredEndpoints?.length;
            }

            index++;
        }

        this.pathCounter.total = this.endpoints?.length;
    }

    /**
     * Find groups.
     * @param formGroups
     * @private
     */
    private allValuesAreTrue(formGroups: any[]): boolean {
        return formGroups.every(item => Object.values(item.value?.methods).every(item => item === true));
    }

    /**
     * Create a checkbox entry for each formatted group.
     * @private
     */
    private createCheckboxFormGroupEntry(allSelected: boolean): void {
        this.formCheckBoxes.push(
            this.formBuilder.group({
                POST: [false],
                GET: [false],
                PUT: [false],
                PATCH: [false],
                DELETE: [false],
                ALL: [allSelected]
            })
        );
    }

    /**
     * Filer endpoint by group.
     * @param service
     * @private
     */
    private filterEndpointsByGroup(service: any): any[] {
        return this.formEndpoints.controls.filter(
            (control: AbstractControl) => control.value.service.startsWith(service)
        );
    }

    /**
     * Add the new acl rule to the acl rules array.
     * @param endpoint
     * @param index
     * @private
     */
    private addEndpoint(endpoint?: any, index?: number): void {
        this.formEndpoints.push(this.createEndpointGroup(endpoint, index));
    }

    /**
     * Create a row in the form for each endpoint.
     * @param endpoint
     * @param index
     * @private
     */
    private createEndpointGroup(endpoint?: any, index?: number): FormGroup {
        const post = this.getValueFromEndpoint(endpoint, 'POST');
        const get = this.getValueFromEndpoint(endpoint, 'GET');
        const put = this.getValueFromEndpoint(endpoint, 'PUT');
        const patch = this.getValueFromEndpoint(endpoint, 'PATCH');
        const deleteMethod = this.getValueFromEndpoint(endpoint, 'DELETE');

        return this.formBuilder.group({
            path: [endpoint?.path],
            service: [endpoint?.service],
            index: [index], // Create index here as loosing scope in the two ngFor loops in the view.
            friendlyName: [endpoint?.friendlyName],
            methods: this.formBuilder.group({
                POST: [{ value: post, disabled: post === null }],
                GET: [{ value: get, disabled: get === null }],
                PUT: [{ value: put, disabled: put === null }],
                PATCH: [{ value: patch, disabled: patch === null }],
                DELETE: [{ value: deleteMethod, disabled: deleteMethod === null }]
            })
        });
    }

    /**
     * When disabled is triggered reset each toggle state to false
     * @private
     */
    private resetToggleState(): void {
        for (const toggle in this.toggleState) {
            if (this.toggleState.hasOwnProperty(toggle)) {
                this.toggleState[toggle] = false;
            }
        }
    }

    /**
     * Get value from endpoint.
     * @param endpoint
     * @param method
     * @private
     */
    private getValueFromEndpoint(endpoint: any, method: string): any {
        return endpoint?.methods?.[method] === true || endpoint?.methods?.[method] === false
            ? endpoint?.methods?.[method]
            : null;
    }
}
